import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { SmallInputLabel } from "src/components";
import { useGetAzureDevOpsWorkItemCollection } from "src/http";

interface Props {
  visible: boolean;
  azureDevOpsWorkItemCollectionGuid: string | null;
  onAddClicked: (workItemId: number, includeChildren: boolean) => void;
  onCancelClicked: () => void;
}

interface AddAzureDevOpsWorkItemFormState {
  workItemId: string;
  includeChildren: boolean;
}

const AddAzureDevOpsWorkItemModal = (props: Props) => {
  const { data } = useGetAzureDevOpsWorkItemCollection(props.azureDevOpsWorkItemCollectionGuid);
  const { formState, control, handleSubmit, reset, setFocus } = useForm<AddAzureDevOpsWorkItemFormState>({
    defaultValues: {
      workItemId: "",
      includeChildren: false,
    },
  });

  useEffect(() => {
    if (props.visible) {
      setTimeout(() => {
        setFocus("workItemId");
      }, 250);
    }
  }, [props.visible, setFocus]);

  useEffect(() => {
    reset({}, { keepDefaultValues: true });
  }, [props.visible, reset]);

  function handleCancelClicked() {
    props.onCancelClicked();
  }

  function handleDoneClicked() {
    handleSubmit((formState) => {
      const workItemId = parseWorkItemId(formState.workItemId);

      if (workItemId) {
        props.onAddClicked(workItemId, formState.includeChildren);
      } else {
        throw new Error("WorkItemId is invalid.");
      }
    })();
  }

  function parseWorkItemId(value: string) {
    if (/^\d+$/i.test(value)) {
      return parseInt(value);
    } else {
      return false;
    }
  }

  return (
    <Dialog open={props.visible} onClose={handleCancelClicked}>
      <DialogTitle>Add Azure DevOps Work Item</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Stack>
            <SmallInputLabel>Work Item ID</SmallInputLabel>
            <Controller
              name="workItemId"
              control={control}
              rules={{
                required: { message: "Work Item ID is required", value: true },
                validate: (value) => {
                  const workItemId = parseWorkItemId(value);

                  if (workItemId) {
                    if (data && data.some((x) => x.workItemId === workItemId)) {
                      return "Work Item is already linked";
                    } else {
                      return true;
                    }
                  } else {
                    return "Work Item ID is invalid";
                  }
                },
              }}
              render={({ field }) => (
                <TextField
                  inputRef={field.ref}
                  placeholder="Work Item ID"
                  value={field.value}
                  onChange={field.onChange}
                  error={formState.errors["workItemId"] !== undefined}
                  helperText={formState.errors["workItemId"]?.message}
                />
              )}
            />
          </Stack>

          <Controller
            name="includeChildren"
            control={control}
            render={({ field }) => (
              <RadioGroup value={field.value}>
                <FormControlLabel
                  label="Add linked item"
                  value={false}
                  control={<Radio />}
                  onChange={() => field.onChange(false)}
                />
                <FormControlLabel
                  label="Add linked item and its children"
                  value={true}
                  control={<Radio />}
                  onChange={() => field.onChange(true)}
                />
              </RadioGroup>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleDoneClicked}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AddAzureDevOpsWorkItemModal };
